<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>
  <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>

  <div class="container mt-4">
    <h3>Terminaler</h3>

    <!-- SUPER_ADMIN: which card payment the tills and kiosks use, per LOCATION — the setting is on the location, so
         every till and kiosk there follows it. Replaces the N1QL in CagistaServices docs/viva-legacy-merchant-switch.md §7. -->
    <section v-if="isSuperAdmin() && paymentLocations.length" class="tp-section mt-3">
      <div class="d-flex align-items-center flex-wrap" style="gap:0.5rem">
        <span class="fw-semibold">Kortbetaling i kasse og kiosk</span>
        <span class="tp-tag">kun SUPER_ADMIN</span>
      </div>
      <div class="tp-intro mt-1">
        Gjelder hele avdelingen: alle kasser og kiosker der følger samme oppsett.
      </div>
      <div class="tp-grid mt-2">
        <TillPaymentPanel v-for="loc in paymentLocations" :key="loc.id" :location="loc" />
      </div>
    </section>

    <div class="table-responsive-sm mt-4">
      <table class="table table-striped">
        <thead>
        <!-- Every column sorts on click: first click ascending, second flips. Default is Terminal-ID
             ascending — the number printed on the device is how staff refer to it. -->
        <tr>
          <th v-for="col in visibleColumns" :key="col.key" class="tm-th-sort" role="button"
              :aria-sort="sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'"
              @click="sortBy(col.key)">
            {{ col.label }}
            <font-awesome-icon class="tm-sort-caret" :class="{ 'tm-sort-caret--active': sortKey === col.key }"
              :icon="['fas', sortKey !== col.key ? 'sort' : (sortDir === 'asc' ? 'sort-up' : 'sort-down')]" />
          </th>
          <th v-if="isSuperAdmin()">DocumentId</th>
          <th v-if="isSuperAdmin()"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="item in sortedItems" :key="item.id" :class="{ 'tm-row--hidden': item.hidden }">
          <td v-if="hasMultiLocations()">
            <span>{{ item.locationName }}</span>
          </td>
          <td>
            <span>{{ item.systemIdNr }}</span>
          </td>
          <td>
            <span>{{ item.terminalId }}</span>
          </td>
          <td>
            <span>{{ item.terminalType }}</span>
          </td>
          <td>
            <span>{{ item.deviceName }}</span>
            <!-- Only SUPER_ADMIN ever gets these rows; the merchant's own pages leave them out. -->
            <div v-if="item.hidden" class="tm-hidden-note">
              <span class="cp-badge">Skjult for merchant</span>
              <span v-if="item.hiddenTime"> {{ formatTime(item.hiddenTime) }} av {{ item.hiddenBy }}</span>
            </div>
          </td>
          <!-- Is the terminal connected to Sync Gateway, in its own words (reported with every order-sync poll from
               TastyPOS 1.6.33). For the merchant too: a till that has quietly stopped syncing is theirs to know about. -->
          <td>
            <span class="ts-badge" :class="'ts-badge--' + syncOf(item).tone">{{ syncOf(item).label }}</span>
            <div v-if="syncOf(item).detail" class="ts-detail">{{ syncOf(item).detail }}</div>
            <div v-if="syncOf(item).tech" class="ts-tech">{{ syncOf(item).tech }}</div>
          </td>
          <!-- Which terminal prints what. Read-only for everyone: when a ticket is missing, "which
               printer was supposed to have it" is the first question, and it should not need SUPER_ADMIN
               to answer. -->
          <td>
            <span v-if="!item.useAsCloudPrinter" class="text-muted">—</span>
            <template v-else>
              <span class="cp-badge">CloudPRNT</span>
              <div class="cp-filter-text">{{ printerFilterText(item) }}</div>
            </template>
          </td>
          <td>
            <span>{{ item.createdDateTime }}</span>
          </td>
          <td v-if="isSuperAdmin()">
            <span>{{ item.id }}</span>
          </td>
          <td v-if="isSuperAdmin()">
            <button class="bo_btn mb-3 d-block" @click="openMigrateModal(item)">Migrer</button>
            <button class="bo_btn mb-3 d-block" @click="openChangeIdModal(item)">Endre properties</button>
            <button class="bo_btn mb-3 d-block" @click="openResyncModal(item)">Resync</button>
            <button class="bo_btn_secondary d-block" :disabled="hidingId === item.id" @click="toggleHidden(item)">
              {{ item.hidden ? 'Vis for merchant' : 'Skjul for merchant' }}
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>

  <MigrateTerminalModal ref="migrateModal" @migrated="onMigrated" />
  <ChangeTerminalPropertiesModal ref="changePropertiesModal" @changed="onChanged" />
  <ForceResyncModal ref="resyncModal" @closed="loadSyncStates" />
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";
import MigrateTerminalModal from "@/components/terminal/MigrateTerminalModal";
import ChangeTerminalPropertiesModal from "@/components/terminal/ChangeTerminalPropertiesModal";
import ForceResyncModal from "@/components/terminal/ForceResyncModal";
import TillPaymentPanel from "@/components/terminal/TillPaymentPanel";
import TerminalService from "@/services/terminal/TerminalService";
import TextUtil from "@/util/TextUtil";
import LocationUtil from "@/util/LocationUtil";
import UserUtil from "@/util/UserUtil";
import TerminalVisibility from "@/util/TerminalVisibility";
import TerminalSyncStatus from "@/util/TerminalSyncStatus";

/** How often the «Synk» column is refreshed while the page is open — the terminals report every ~30 s. */
const SYNC_REFRESH_MS = 30000;

export default {
  components: {LoadingSpinner, ConfirmDialogue, MigrateTerminalModal, ChangeTerminalPropertiesModal, ForceResyncModal, TillPaymentPanel},
  data() {
    return {
      sortKey: 'terminalId',
      sortDir: 'asc',
      loading: true,
      hidingId: '',
      items: [],
      // Sync Gateway state per terminal document id, and the server's clock when it was read.
      syncStates: {},
      syncServerTime: '',
      syncTimer: null
    };
  },
  mounted() {
    this.loadTerminals();
    this.loadSyncStates();
    this.syncTimer = setInterval(this.loadSyncStates, SYNC_REFRESH_MS);
  },
  beforeUnmount() {
    clearInterval(this.syncTimer);
  },
  computed: {
    /** Every location of the merchant — one without a till yet can still be set up before its first one arrives. */
    paymentLocations() {
      let locations = [];
      try {
        locations = LocationUtil.getLocations() || [];
      } catch (e) {
        return [];
      }
      return locations.filter((l) => l && l.id)
        .map((l) => ({ id: l.id, name: l.name, shortName: l.shortName }));
    },
    /** The sortable columns in display order; Avdeling only when the merchant has several. */
    visibleColumns() {
      const cols = [
        { key: 'locationName', label: 'Avdeling', when: this.hasMultiLocations() },
        { key: 'systemIdNr', label: 'ID-nummer' },
        { key: 'terminalId', label: 'Terminal-ID' },
        { key: 'terminalType', label: 'Type' },
        { key: 'deviceName', label: 'Enhetsnavn' },
        { key: 'sync', label: 'Synk' },
        { key: 'printer', label: 'Skriver' },
        { key: 'createdRaw', label: 'Opprettet' }
      ];
      return cols.filter((c) => c.when !== false);
    },
    /**
     * items in the chosen order. Empty values go last whichever way the sort runs, numbers compare as
     * numbers, text with Norwegian collation and numeric awareness ("Kasse 2" before "Kasse 10"), and
     * the original index breaks ties so equal rows never shuffle between renders.
     */
    sortedItems() {
      const key = this.sortKey;
      const dir = this.sortDir === 'desc' ? -1 : 1;
      const collator = new Intl.Collator('nb', { numeric: true, sensitivity: 'base' });
      const valueOf = (item) => {
        if (key === 'printer') return item.useAsCloudPrinter ? this.printerFilterText(item) : '';
        // Worst first when ascending: not connected, no contact, not reporting, unknown, connected.
        if (key === 'sync') return this.syncOf(item).rank;
        const v = item[key];
        return v === null || v === undefined ? '' : v;
      };
      const empty = (v) => v === '' || v === null || v === undefined;
      return this.items
        .map((item, i) => ({ item, i, v: valueOf(item) }))
        .sort((a, b) => {
          const ae = empty(a.v), be = empty(b.v);
          if (ae !== be) return ae ? 1 : -1;
          if (ae && be) return a.i - b.i;
          const c = typeof a.v === 'number' && typeof b.v === 'number'
            ? a.v - b.v
            : collator.compare(String(a.v), String(b.v));
          return c !== 0 ? c * dir : a.i - b.i;
        })
        .map((x) => x.item);
    }
  },
  methods:{
    /** Click the active column to flip it; click another to sort by it ascending. */
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
    },
    loadTerminals(){
      this.loading = true;
      this.items = [];
      TerminalService.getTerminals().then(
          (response) => {
            // A terminal SUPER_ADMIN hid is not the merchant's to see; SUPER_ADMIN sees it, marked.
            let terminals = TerminalVisibility.forMerchantView(response.data);
            for (var i = 0; i < terminals.length; i++) {
              let terminal = terminals[i];
              this.items.push({
                'id': terminal.id,
                'locationId': terminal.locationId,
                'locationName': this.getLocationName(terminal.locationId),
                'systemIdNr': terminal.systemIdNr,
                'terminalId': terminal.terminalId,
                'terminalType': terminal.terminalType,
                'deviceName': terminal.deviceName,
                'createdDateTime': TextUtil.getCreatedTimeText(terminal.createdDateTime),
                // The raw value, for sorting — the formatted text above does not order chronologically.
                'createdRaw': terminal.createdDateTime || '',
                'useAsCloudPrinter': terminal.useAsCloudPrinter || false,
                'cloudPrinterChannels': Array.isArray(terminal.cloudPrinterChannels) ? terminal.cloudPrinterChannels : [],
                'cloudPrinterOrderTypes': Array.isArray(terminal.cloudPrinterOrderTypes) ? terminal.cloudPrinterOrderTypes : [],
                'hidden': terminal.hidden === true,
                'hiddenTime': terminal.hiddenTime || '',
                'hiddenBy': terminal.hiddenBy || ''
              });
            }

            this.loading = false;
          },
          (error) => {
            console.log(error);
          }
      );
    },
    /**
     * The «Synk» column. A failed read keeps the last answer on screen — a column that blanks out every time the
     * network hiccups would look like every till just disconnected.
     */
    loadSyncStates(){
      TerminalService.getTerminalSyncStates().then(
          (response) => {
            const data = response && response.data ? response.data : {};
            const byId = {};
            (Array.isArray(data.terminals) ? data.terminals : []).forEach((t) => { byId[t.terminalDocumentId] = t; });
            this.syncStates = byId;
            this.syncServerTime = data.serverTime || '';
          },
          (error) => {
            console.log(error);
          }
      );
    },
    syncOf(item){
      return TerminalSyncStatus.describe(this.syncStates[item.id], this.syncServerTime, this.isSuperAdmin(), item.terminalType);
    },
    isSuperAdmin(){
      return UserUtil.isSuperAdmin();
    },
    hasMultiLocations(){
      return LocationUtil.hasMultiLocations();
    },
    getLocationName(locationId){
      let location = LocationUtil.getLocation(locationId);
      return location.name;
    },
    openMigrateModal(item){
      this.$refs.migrateModal.open(item.terminalId, item.locationId);
    },
    openChangeIdModal(item){
      this.$refs.changePropertiesModal.open(item.id, item.terminalId, item.deviceName, item.useAsCloudPrinter,
          item.cloudPrinterChannels, item.cloudPrinterOrderTypes, item.terminalType, item.locationId, item.kdsRole);
    },
    /**
     * "Nettbutikk, Kiosk · Ta med, Levere" — or "Alle bestillinger" for a printer with no filter, which
     * is every printer configured before the filter existed and means exactly that.
     */
    printerFilterText(item){
      const channelLabel = { WEBSHOP: 'Nettbutikk', POS: 'Kasse', KIOSK: 'Kiosk' };
      const typeLabel = { DINE_IN: 'Spise her', PICKUP: 'Ta med', DELIVERY: 'Levere' };
      const channels = [...new Set(item.cloudPrinterChannels.map(c => channelLabel[c] || c))];
      const types = item.cloudPrinterOrderTypes.map(t => typeLabel[t] || t);
      if (channels.length === 0 && types.length === 0) return 'Alle bestillinger';
      return (channels.length ? channels.join(', ') : 'Alle kanaler')
          + ' · ' + (types.length ? types.join(', ') : 'Alle ordretyper');
    },
    onMigrated(){
      this.loadTerminals();
    },
    onChanged(){
      this.loadTerminals();
    },
    openResyncModal(item){
      this.$refs.resyncModal.open(item.id, item.terminalId, item.deviceName, this.syncStates[item.id]);
    },
    /**
     * Hide a terminal from the merchant — a test till at their location — or show it again. Visibility only: the
     * terminal, its journal, its Z-reports and the device's login stay as they are, so it can be used again.
     */
    async toggleHidden(item){
      const name = '«' + (item.deviceName || item.terminalType) + '» (terminal ' + item.terminalId + ')';
      const hide = !item.hidden;
      const ok = await this.$refs.confirmDialogue.show(hide ? {
        title: 'Skjul for merchant',
        message: name + ' forsvinner fra merchantens sider: Terminaler, X- og Z-rapport, omsetningsrapport, '
          + 'kontantbeholdning, SAF-T og kassetellingen, og den faktureres ikke. Ingenting slettes — kassen virker som '
          + 'før, Z-rapporten tas fortsatt, og du ser den fortsatt på alle sidene. Du kan vise den igjen når som helst.',
        okButton: 'Skjul',
        cancelButton: 'Avbryt'
      } : {
        title: 'Vis for merchant',
        message: name + ' vises igjen for merchanten på alle sidene.',
        okButton: 'Vis',
        cancelButton: 'Avbryt'
      });
      if (!ok) return;
      this.hidingId = item.id;
      TerminalService.setTerminalHidden(item.id, hide).then(
        () => {
          this.hidingId = '';
          this.loadTerminals();
        },
        (err) => {
          this.hidingId = '';
          const message = (err.response && err.response.data && err.response.data.error) || 'Det skjedde en feil. Prøv igjen.';
          this.$refs.confirmDialogue.show({ title: 'Kunne ikke endre', message: message, okButton: 'OK' });
        }
      );
    },
    // 14.10.2026 06:10, whatever the ICU version.
    formatTime(iso){
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      const pad = (n) => String(n).padStart(2, '0');
      return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear()
        + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
    }
  }
};
</script>

<style scoped>
.tp-section { border: 1px dashed var(--bo-line); border-radius: 10px; padding: 12px 14px; background: var(--bo-surface-2); }
.tp-tag { font-size: 0.72rem; color: var(--bo-muted); background: var(--bo-surface); border-radius: 20px; padding: 2px 10px; }
.tp-intro { font-size: 0.85rem; color: var(--bo-ink-2); }
.tp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr)); gap: 0.75rem; }
.tm-th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.tm-sort-caret {
  font-size: 0.75em;
  margin-left: 4px;
  opacity: 0.35;
}
.tm-sort-caret--active {
  opacity: 1;
  color: #ECB109;
}
.cp-badge {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--bo-sunk, #f1f3f5);
  color: var(--bo-ink-2, #495057);
}
.tm-row--hidden td { color: var(--bo-muted, #6c757d); }
.tm-hidden-note { font-size: 0.8rem; color: var(--bo-muted, #6c757d); margin-top: 2px; }
.ts-badge {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 20px;
  border: 1px solid transparent;
  white-space: nowrap;
}
.ts-badge--ok { background: var(--bo-ok-bg); color: var(--bo-ok-fg); border-color: var(--bo-ok-line); }
.ts-badge--warn { background: var(--bo-warn-bg); color: var(--bo-warn-fg); border-color: var(--bo-warn-line); }
.ts-badge--bad { background: var(--bo-bad-bg); color: var(--bo-bad-fg); border-color: var(--bo-bad-line); }
.ts-badge--muted { background: var(--bo-sunk, #f1f3f5); color: var(--bo-ink-2, #495057); }
.ts-detail {
  font-size: 0.85rem;
  color: var(--bo-muted, #6c757d);
  margin-top: 2px;
}
.ts-tech {
  font-size: 0.78rem;
  color: var(--bo-muted, #6c757d);
  margin-top: 2px;
  word-break: break-word;
  max-width: 28rem;
}
.cp-filter-text {
  font-size: 0.85rem;
  color: var(--bo-muted, #6c757d);
  margin-top: 2px;
}
</style>
